import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { getStatusRemarkEntries } from '../../utils/statusRemarks';
import { formatRemarkDateTime } from '../../utils/dateFormat';
import { addStatusRemark } from '../../services/candidateService';
import { showError, showSuccess } from '../../utils/toast';
import { getApiErrorMessage } from '../../utils/apiError';

function StatusRemarkHistory({
  candidateId,
  statusEntry,
  onRemarkAdded,
  canAddRemark = false,
}) {
  const [showForm, setShowForm] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const [saving, setSaving] = useState(false);

  const remarks = getStatusRemarkEntries(statusEntry);
  const statusEntryId = statusEntry?._id;

  useEffect(() => {
    if (!canAddRemark) {
      setShowForm(false);
      setRemarkText('');
    }
  }, [canAddRemark, statusEntryId]);

  const handleCancel = () => {
    setShowForm(false);
    setRemarkText('');
  };

  const handleSubmit = async () => {
    const trimmed = remarkText.trim();
    if (!trimmed) {
      showError('Remark is required.');
      return;
    }
    if (!candidateId || !statusEntryId) {
      showError('Unable to add remark for this status entry.');
      return;
    }

    setSaving(true);
    try {
      const response = await addStatusRemark(candidateId, statusEntryId, trimmed);
      showSuccess('Remark added successfully ✓');
      setRemarkText('');
      setShowForm(false);
      onRemarkAdded?.(response.statusEntry, response.candidate);
    } catch (error) {
      showError(getApiErrorMessage(error, 'Failed to add remark. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td colSpan={2} className="p-0 border-0">
        <div className="hr-status-remarks">
          <div className="hr-status-remarks-title"><b>Remarks:</b></div>
          {remarks.length === 0 ? (
            <p className="hr-status-remarks-empty text-muted mb-2">No remarks yet.</p>
          ) : (
            <ul className="hr-status-remarks-list">
              {remarks.map((remark, index) => (
                <li key={`${statusEntryId || 'status'}-remark-${index}`} className="hr-status-remark-item">
                  <div className="hr-status-remark-text">{remark.text}</div>
                  <div className="hr-status-remark-date">
                    {formatRemarkDateTime(remark.createdAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {canAddRemark && (
            !showForm ? (
              <div className="hr-status-remarks-actions">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowForm(true)}
                  disabled={!candidateId || !statusEntryId || saving}
                >
                  + Add Remark
                </Button>
              </div>
            ) : (
              <div className="hr-status-remark-form">
                <div className="hr-status-remark-form-title">Add Remark</div>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={remarkText}
                  onChange={(event) => setRemarkText(event.target.value)}
                  placeholder="Enter new remark..."
                  disabled={saving}
                />
                <div className="hr-status-remark-form-actions">
                  <Button variant="outline-secondary" size="sm" onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Adding...' : 'Add Remark'}
                  </Button>
                </div>
              </div>
            )
          )}
        </div>
      </td>
    </tr>
  );
}

export default StatusRemarkHistory;
